import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users, Info } from "lucide-react";
import { useSite } from "../hooks/useSite";
import { imgUrl } from "../lib/api";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { WhatsAppFloat } from "../components/site/WhatsAppFloat";
import { RoomCta } from "../components/site/Accommodations";
import { Lightbox } from "../components/site/Lightbox";
import { Seo } from "../components/site/Seo";
import { SiteLoader } from "../components/site/Misc";

const RoomPhotos = ({ room }) => {
  const [idx, setIdx] = useState(null);
  if (!room.photos.length) return null;
  const [main, ...rest] = room.photos;
  return (
    <div className="grid grid-cols-4 gap-2 sm:gap-3">
      <button onClick={() => setIdx(0)} data-testid="room-photo-0" className="col-span-4 aspect-[16/10] overflow-hidden bg-[#111111] group">
        <img src={imgUrl(main.src)} alt={main.title || room.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </button>
      {rest.slice(0, 8).map((p, i) => (
        <button key={p.id} onClick={() => setIdx(i + 1)} data-testid={`room-photo-${i + 1}`} className="aspect-square overflow-hidden bg-[#111111] group">
          <img src={imgUrl(p.thumb)} alt={p.title || room.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        </button>
      ))}
      <Lightbox photos={room.photos} index={idx} onClose={() => setIdx(null)} onIndex={setIdx} />
    </div>
  );
};

export default function AccommodationPage() {
  const { slug } = useParams();
  const { data, error } = useSite(`/public/accommodations/${slug}`);
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center text-center px-6" data-testid="room-not-found">
        <div><p className="font-display text-3xl">Acomodação não encontrada</p><Link to="/" className="btn-outline-gold mt-8">Voltar ao início</Link></div>
      </div>
    );
  }
  if (!data) return <SiteLoader />;
  const c = data.content;
  const room = data.accommodation;
  return (
    <div className="bg-[#000000] text-zinc-100" data-testid="accommodation-page">
      <Seo content={c} title={`${room.name} | Vienna Palace Hotel`} description={(room.description || c.seo_description).slice(0, 160)} image={room.photos[0]?.src} />
      <Header content={c} hasServices={(c.services || []).length > 0} onHome={false} />
      <main className="pt-28 pb-24 max-w-7xl mx-auto px-5 lg:px-10">
        <Link to="/#acomodacoes" data-testid="room-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#E6C687]"><ArrowLeft size={16} /> Todas as acomodações</Link>
        <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-12">
          <RoomPhotos room={room} />
          <div className="lg:sticky lg:top-28 self-start">
            <p className="font-caps text-[11px] text-[#D4AF37]">Acomodação</p>
            <h1 data-testid="room-title" className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">{room.name}</h1>
            <div className="w-16 h-px bg-[#D4AF37]/70 mt-6" />
            {room.description && <p className="mt-6 text-zinc-300 leading-relaxed whitespace-pre-line" data-testid="room-description">{room.description}</p>}
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-zinc-400">
              {room.capacity && <span className="flex items-center gap-2"><Users size={15} className="text-[#D4AF37]" /> {room.capacity}</span>}
              {room.price && <span className="font-display text-2xl text-[#E6C687]" data-testid="room-price">{room.price}</span>}
            </div>
            {room.amenities?.length > 0 && (
              <div className="mt-8">
                <p className="font-caps text-[10px] text-zinc-500">Comodidades</p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {room.amenities.map((a) => <li key={a} className="text-xs px-3 py-1.5 rounded-full border border-zinc-700 text-zinc-300">{a}</li>)}
                </ul>
              </div>
            )}
            {room.notes && <p className="mt-8 flex gap-3 text-sm text-zinc-400 whitespace-pre-line"><Info size={16} className="text-[#D4AF37] shrink-0 mt-0.5" /> {room.notes}</p>}
            <RoomCta content={c} room={room} className="mt-10 w-full sm:w-auto" />
          </div>
        </div>
      </main>
      <Footer content={c} />
      <WhatsAppFloat content={c} />
    </div>
  );
}
